import ApiService from "../../../services/ApiService";
import { normalizeHomeV2PagedResponse } from "../../../services/HomeService";
import { getContentLanguage } from "../../../services/LangPrefs";
import Logger from "../../../services/Logger";
import { resolveJigsawTemplateTileSize } from "./jigsawBrowseUtils";

const BROWSE_BASE = "/home/jigsaw";

/** Extract `{ story, storyJigsaw, templates }` from decrypted jigsaw group payloads. */
export function normalizeHomeJigsawGroup(raw) {
    if (!raw || typeof raw !== "object") return null;
    if (raw.code === "FAILURE") return null;

    const extract = (o) => {
        if (!o || typeof o !== "object") return null;
        const templates = Array.isArray(o.templates) ? o.templates : [];
        const story = o.story ?? null;
        const sid = o.storyId ?? o.story_id ?? story?.id ?? null;
        const unavailable = o.unavailable === true;
        const unavailableMessage =
            typeof o.unavailableMessage === "string"
                ? o.unavailableMessage
                : typeof o.unavailable_message === "string"
                  ? o.unavailable_message
                  : null;
        if (!story && templates.length === 0 && !sid && !unavailable) return null;

        const sj = o.storyJigsaw ?? o.story_jigsaw ?? null;
        let storyJigsaw = null;
        if (sj && typeof sj === "object") {
            storyJigsaw = {
                id: sj.id ?? null,
                free: sj.free === true,
                editorsPicks: sj.editorsPicks === true || sj.editors_choice === true,
                editorsPicksAt: sj.editorsPicksAt ?? sj.editors_choice_at ?? null,
                publishedAt: sj.publishedAt ?? sj.published_at ?? null,
                state: sj.state ?? null,
                disabledAt: sj.disabledAt ?? sj.disabled_at ?? null,
            };
        }

        const normalizedTemplates = templates.map((t) => {
            if (!t || typeof t !== "object") return t;
            const base = {
                ...t,
                tileSize: t.tileSize ?? t.tile_size ?? null,
                difficultyLevel: t.difficultyLevel ?? t.difficulty_level ?? null,
                templateName: t.templateName ?? t.template_name ?? t.name ?? null,
            };
            const resolvedTile = resolveJigsawTemplateTileSize(base);
            return {
                ...base,
                tileSize: resolvedTile > 0 ? resolvedTile : base.tileSize,
            };
        });

        const userPlays = Array.isArray(o.userPlays)
            ? o.userPlays
            : Array.isArray(o.user_plays)
              ? o.user_plays
              : null;

        return {
            storyId: typeof sid === "string" ? sid.trim() : sid,
            story,
            storyJigsaw,
            templates: normalizedTemplates,
            ...(userPlays ? { userPlays } : {}),
            ...(unavailable ? { unavailable: true } : {}),
            ...(unavailableMessage ? { unavailableMessage } : {}),
            puzzleTemplate: o.puzzleTemplate ?? o.puzzle_template ?? null,
            playCount: (() => {
                const raw = o.playCount ?? o.play_count;
                if (raw == null || raw === "") return 0;
                const n = Number(raw);
                return Number.isFinite(n) && n >= 0 ? n : 0;
            })(),
        };
    };

    const direct = extract(raw);
    if (direct) return direct;

    const nestedKeys = ["data", "content", "payload", "body", "result", "jigsawStoryGroup"];
    for (const key of nestedKeys) {
        const inner = raw[key];
        if (inner && typeof inner === "object") {
            const u = extract(inner);
            if (u) return u;
        }
    }
    return null;
}

export function jigsawBundleState(group) {
    return String(group?.storyJigsaw?.state || "").toUpperCase();
}

/** Disabled or discarded story: app cannot start or continue play. */
export function isJigsawGroupUnavailable(group) {
    if (!group || typeof group !== "object") return false;
    if (group.unavailable === true) return true;
    const state = jigsawBundleState(group);
    return state === "DISABLED" || state === "DISCARDED";
}

export function isJigsawGroupDiscarded(group) {
    return jigsawBundleState(group) === "DISCARDED";
}

async function listHomeFeatured(path, { language, limit = 5 } = {}) {
    try {
        const params = new URLSearchParams();
        const lang = language ?? getContentLanguage();
        if (lang != null && String(lang).trim() !== "") {
            params.set("language", String(lang).trim());
        }
        const cap = Math.max(1, Math.min(Number(limit) || 5, 20));
        params.set("limit", String(cap));
        const data = await ApiService.getData(`${BROWSE_BASE}/${path}?${params.toString()}`);
        if (data && data.code === "FAILURE") {
            return { language: lang, limit: cap, content: [] };
        }
        const content = Array.isArray(data?.content)
            ? data.content.map((row) => normalizeHomeJigsawGroup(row)).filter(Boolean)
            : [];
        return {
            language: data?.language ?? lang,
            limit: data?.limit ?? cap,
            content,
        };
    } catch (error) {
        Logger.error(`JigsawService.${path}:`, error);
        return { language: language ?? getContentLanguage(), limit: 5, content: [] };
    }
}

/**
 * Home jigsaw browse REST (`/home/jigsaw`).
 */
const JigsawService = {
    /**
     * GET /home/jigsaw — published jigsaw puzzles by story group (paged).
     * @param {object} params — page, pageSize, language?, free?, excludeCompleted?, excludePlayed?, sort?, keyword?, tags?
     */
    listBrowse: async ({
        page = 0,
        pageSize = 12,
        free,
        language,
        excludeCompleted,
        excludePlayed,
        sort,
        keyword,
        tags,
    } = {}) => {
        try {
            const params = new URLSearchParams({
                page: String(page),
                pageSize: String(pageSize),
            });
            const lang = language ?? getContentLanguage();
            if (lang != null && String(lang).trim() !== "") {
                params.set("language", String(lang).trim());
            }
            if (free === true || free === false) {
                params.set("free", String(free));
            }
            if (excludeCompleted === true || excludeCompleted === false) {
                params.set("excludeCompleted", String(excludeCompleted));
            }
            if (excludePlayed === true || excludePlayed === false) {
                params.set("excludePlayed", String(excludePlayed));
            }
            if (sort != null && String(sort).trim() !== "") {
                params.set("sort", String(sort).trim());
            }
            if (keyword != null && String(keyword).trim() !== "") {
                params.set("keyword", String(keyword).trim());
            }
            const tagList = Array.isArray(tags) ? tags : [];
            tagList.forEach((t) => {
                if (t != null && String(t).trim() !== "") {
                    params.append("tags", String(t).trim());
                }
            });
            const data = await ApiService.getData(`${BROWSE_BASE}?${params.toString()}`);
            const normalized = normalizeHomeV2PagedResponse(data);
            const content = Array.isArray(normalized.content)
                ? normalized.content.map((row) => normalizeHomeJigsawGroup(row)).filter(Boolean)
                : [];
            return { ...normalized, content };
        } catch (error) {
            Logger.error("JigsawService.listBrowse:", error);
            return { content: [], totalElements: 0, totalPages: 0, number: 0, size: 0, hasNext: false };
        }
    },

    /**
     * GET /home/jigsaw/popular — published stories ranked by play count over the last 7 days (default 5).
     * @param {{ language?: string, limit?: number }} [params]
     */
    listPopular: async ({ language, limit = 5 } = {}) => listHomeFeatured("popular", { language, limit }),

    /**
     * GET /home/jigsaw/editors-picks — checked puzzles, newest check first (default 5).
     * @param {{ language?: string, limit?: number }} [params]
     */
    listEditorsPicks: async ({ language, limit = 5 } = {}) =>
        listHomeFeatured("editors-picks", { language, limit }),

    /**
     * GET /home/jigsaw/distinct-tags — `[{ tag, count }, …]` for published jigsaw-linked stories.
     * @param {object} params — language?, state? (default PUBLISHED)
     */
    getDistinctTags: async ({ language, state = "PUBLISHED" } = {}) => {
        try {
            const params = new URLSearchParams();
            const lang = language ?? getContentLanguage();
            if (lang != null && String(lang).trim() !== "") {
                params.set("language", String(lang).trim());
            }
            if (state != null && String(state).trim() !== "") {
                params.set("state", String(state).trim());
            }
            const qs = params.toString() ? `?${params.toString()}` : "";
            const data = await ApiService.getData(`${BROWSE_BASE}/distinct-tags${qs}`);
            if (data && data.code === "FAILURE") {
                return {
                    success: false,
                    error: data.message || "Failed to fetch jigsaw tags",
                    data: [],
                };
            }
            const list = Array.isArray(data) ? data : [];
            return { success: true, data: list };
        } catch (error) {
            Logger.error("JigsawService.getDistinctTags:", error);
            return {
                success: false,
                error: error.message || "Failed to fetch jigsaw tags",
                data: [],
            };
        }
    },

    /**
     * GET /home/jigsaw/newest — newest published story group (JigsawStoryGroupRo).
     * @param {{ language?: string, excludeCompleted?: boolean }} [params]
     */
    getNewest: async ({ language, excludeCompleted = false } = {}) => {
        try {
            const params = new URLSearchParams();
            const lang = language ?? getContentLanguage();
            if (lang != null && String(lang).trim() !== "") {
                params.set("language", String(lang).trim());
            }
            if (excludeCompleted === true || excludeCompleted === false) {
                params.set("excludeCompleted", String(excludeCompleted));
            }
            const qs = params.toString() ? `?${params.toString()}` : "";
            const response = await ApiService.getData(`${BROWSE_BASE}/newest${qs}`);
            if (response && (response.code === "FAILURE" || response.code === "NO_DATA")) {
                return {
                    success: false,
                    error: response.message || "No newest jigsaw puzzle",
                    data: null,
                };
            }
            const group = normalizeHomeJigsawGroup(response) ?? response;
            return { success: true, data: group };
        } catch (error) {
            Logger.error("JigsawService.getNewest:", error);
            return {
                success: false,
                error: error.message || "Failed to fetch newest jigsaw story",
                data: null,
            };
        }
    },

    /** GET /home/jigsaw/free/latest — latest free teaser for landing. */
    getFreeLatest: async ({ language } = {}) => {
        const list = await JigsawService.getFreeFeatured({ language, count: 1 });
        return list[0] ?? null;
    },

    /**
     * Latest free jigsaw story groups for landing (newest first, unique story ids).
     * @param {{ language?: string, count?: number }} opts
     * @returns {Promise<Array<ReturnType<typeof normalizeHomeJigsawGroup>>>}
     */
    getFreeFeatured: async ({ language, count = 2 } = {}) => {
        const limit = Math.max(1, Math.min(Number(count) || 2, 5));
        const lang = language ?? getContentLanguage();
        const seen = new Set();
        const results = [];

        const pushGroup = (group) => {
            if (!group) return;
            const id =
                (typeof group.storyId === "string" && group.storyId.trim()) ||
                (typeof group.story?.id === "string" && group.story.id.trim()) ||
                null;
            if (!id || seen.has(id)) return;
            seen.add(id);
            results.push(group);
        };

        try {
            const params = new URLSearchParams();
            if (lang != null && String(lang).trim() !== "") {
                params.set("language", String(lang).trim());
            }
            const qs = params.toString() ? `?${params.toString()}` : "";
            const raw = await ApiService.getData(`${BROWSE_BASE}/free/latest${qs}`);
            pushGroup(normalizeHomeJigsawGroup(raw));
        } catch (error) {
            Logger.error("JigsawService.getFreeFeatured latest:", error);
        }

        if (results.length < limit) {
            try {
                const page = await JigsawService.listBrowse({
                    page: 0,
                    pageSize: Math.max(limit * 3, 8),
                    free: true,
                    language: lang,
                    sort: "publishedAt:desc",
                });
                for (const row of page.content ?? []) {
                    if (results.length >= limit) break;
                    pushGroup(row);
                }
            } catch (error) {
                Logger.error("JigsawService.getFreeFeatured browse:", error);
            }
        }

        return results.slice(0, limit);
    },

    /** GET /home/jigsaw/packs — published packs and whether this account owns each one. */
    listPacks: async () => {
        const raw = await ApiService.getData("/home/jigsaw/packs");
        if (Array.isArray(raw)) return raw;
        if (raw && Array.isArray(raw.content)) return raw.content;
        throw new Error("Pack list was empty or not a list");
    },
};

export default JigsawService;
